import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { Button, Card, Dropdown, Empty, Spin, Table } from 'antd';
import { EllipsisOutlined, LoadingOutlined } from 'antd/icons';
import styled from '@emotion/styled';
import * as actions from '@actions';
import useWidthBreakpoint from 'vl-common/src/hooks/useWidthBreakpoint';
import useReactiveColumns from 'vl-common/src/hooks/useReactiveColumns';
import usePagedQuery from '@hooks/usePagedQuery';
import useColumnFilters from 'vl-common/src/hooks/useColumnFilters';
import Pagination from '@components/UI/Table/Pagination';
import { useDispatch } from 'react-redux';
import dynamic from 'next/dynamic';
import qs from 'vl-common/src/lib/query-string';
import { type Response as CrQueue } from 'vl-common/src/schemas/getCasesQueues';
import type { AppointmentSecurity } from 'vl-common/src/schemas/getCliniciansApptSec';
import { encodeBase64 } from '@helper';
import Flex from '@components/UI/Flex';

const PatientInfo = dynamic(() => import('@components/Modal/PatientInfo'));

export const AMENDMENT_QUEUE_CODE = 'AMEND';

type AmendmentTableRow = CrQueue['cr_queues'][number];

const ExpandableCard = styled(Card)`
  width: auto;
`;

const EmptyWrapper = styled.div`
  height: 44px;
`;

const StyledSpin = styled(Spin)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledLoadingOutlined = styled(LoadingOutlined)`
  font-size: 30px;
`;

type AmendmentAppointmentsProps = {
  displayFooterCount?: boolean;
  totalQueueCount?: number;
};

const AmendmentAppointments: FC<AmendmentAppointmentsProps> = ({ displayFooterCount, totalQueueCount }) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const currentBreakPoint = useWidthBreakpoint();
  const dispatch = useDispatch();

  const [patientDetails, setPatientDetails] = useState<AppointmentSecurity | null>(null);
  const [showPatientInfoModal, setShowPatientInfoModal] = useState(false);

  const togglePatientInfoModal = () => setShowPatientInfoModal((prev) => !prev);

  const openPatientInfo = async (row: AmendmentTableRow) => {
    const response = await dispatch(actions.dataProtection(row.appt_id));

    setPatientDetails(response?.payload[0]);
    togglePatientInfoModal();
  };

  const navigateToSessionPage = (row: AmendmentTableRow) => {
    router.push(`/clinician/session?req=${encodeBase64(row.appt_id as number)}`);
  };

  const viewTimeline = (row: AmendmentTableRow) => {
    if (!row.case_id || !row.appt_id) return;
    const queryParams = {
      case_id: Buffer.from(`${row.case_id}`).toString('base64'),
      patient_guid: Buffer.from(`${row.user_guid}`).toString('base64')
    };
    router.push(`/clinician/timeline360?${qs.stringify(queryParams)}`);
  };

  const tableActions = (val: AmendmentTableRow) => [
    {
      key: 0,
      label: 'View Appointment',
      'data-testid': 'view-appointment-action',
      onClick: () => navigateToSessionPage(val)
    },
    {
      key: 1,
      label: 'View Timeline',
      'data-testid': 'view-timeline-action',
      onClick: () => viewTimeline(val)
    },
    {
      key: 2,
      label: 'Patient Details',
      'data-testid': 'patient-details-action',
      onClick: () => openPatientInfo(val)
    }
  ];

  const rawColumns = useMemo(
    () => [
      {
        title: 'Client',
        dataIndex: 'client_name',
        key: 'client_name',
        responsive: ['md'],
        onCell: () => ({ 'data-testid': 'client-name-cell' })
      },
      {
        title: 'Case Ref',
        dataIndex: 'client_case_id',
        key: 'client_case_id',
        responsive: ['sm'],
        filterable: { exact: false, queryKey: 'client_case_id' },
        onCell: () => ({ 'data-testid': 'case-ref-cell' })
      },
      {
        title: 'Appt ID',
        dataIndex: 'appt_id',
        key: 'appt_id',
        responsive: ['sm'],
        onCell: () => ({ 'data-testid': 'appt-id-cell' })
      },
      {
        title: 'Patient',
        dataIndex: 'patient_no_title',
        key: 'patient_no_title',
        filterable: { exact: false, queryKey: 'patient_name' },
        onCell: () => ({ 'data-testid': 'patient-cell' })
      },
      {
        title: 'Specialty',
        dataIndex: 'cat_desc',
        key: 'cat_desc',
        onCell: () => ({ 'data-testid': 'specialty-cell' })
      },
      {
        title: 'Actions',
        dataIndex: '',
        render: (val: AmendmentTableRow) => (
          <Dropdown trigger={['click']} menu={{ items: tableActions(val) }} placement="topRight">
            <Button data-testid="actions-menu" type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        ),
        onCell: () => ({ 'data-testid': 'actions-cell' })
      }
    ],
    []
  );

  const {
    columns: tableColumn,
    query,
    handleTableChange,
    pageNumber,
    setPageNumber,
    clearFilters,
    tableKey
  } = useColumnFilters(rawColumns, 10);

  const {
    page: amendments,
    isLoading,
    lastPage
  } = usePagedQuery(
    actions.clinicianCasesQueue({ cr_queue_code: AMENDMENT_QUEUE_CODE, ...query }),
    ['clinicianAppointment', 'amendments', query],
    pageNumber,
    setPageNumber
  );

  const tableFooter = displayFooterCount
    ? () => (
        <span style={{ marginLeft: 48, fontWeight: 500 }}>
          {t('Total amendments:')} {totalQueueCount}
        </span>
      )
    : undefined;

  // @ts-expect-error
  const [displayColumnList, hideColumnList] = useReactiveColumns(tableColumn, currentBreakPoint);

  return (
    <>
      <Flex justify="end" align="center" mb>
        <Button type="primary" onClick={clearFilters} data-testid="reset-amendments-filters">
          {t('Clear Filters')}
        </Button>
      </Flex>
      <Table
        key={tableKey}
        rowKey="case_id"
        columns={displayColumnList}
        dataSource={amendments}
        size="middle"
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (
            <ExpandableCard>
              {hideColumnList.map((item) => (
                // @ts-expect-error
                <p key={item.dataIndex}>
                  {/* @ts-expect-error */}
                  <b>{item.title} :</b> {record[item.dataIndex]}
                </p>
              ))}
            </ExpandableCard>
          ),
          rowExpandable: () => hideColumnList.length > 0
        }}
        locale={{
          emptyText: isLoading ? (
            <EmptyWrapper>
              <StyledSpin indicator={<StyledLoadingOutlined spin />} />
            </EmptyWrapper>
          ) : (
            <Empty description={t('No amendments found')} />
          )
        }}
        onChange={handleTableChange}
        footer={tableFooter}
      />
      <Pagination pageNumber={pageNumber} setPageNumber={setPageNumber} isLastPage={lastPage} />

      {patientDetails && (
        <PatientInfo
          data={patientDetails}
          open={showPatientInfoModal}
          onOk={togglePatientInfoModal}
          onCancel={togglePatientInfoModal}
          okButtonProps={{
            'data-testid': 'close-patient-info'
          }}
        />
      )}
    </>
  );
};

export default AmendmentAppointments;
